import { gql, useSubscription } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import { Battery } from "components/atoms/battery/Battery";
import { UVDIcon } from "components/atoms/icons/UVDPulseIcon/UVDIcon";
import { UVDPulseIcon } from "components/atoms/icons/UVDPulseIcon/UVDPulseIcon";
import { WidgetPaper } from "components/atoms/WidgetPaper";
import { EditRobotNickname } from "components/organisms/panels/EditRobotNickname";
import { UvdRobot } from "gql/graphql";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";
import { useQueryWithSnack } from "utils/useQueryWithSnack";
import { getUvdRobotStatus } from "utils/utilFunctions";

import {
  useUvdConnectionStatus,
  UVDConnectionStatus,
} from "utils/UVD/useUvdConnectionStatus";
import {
  getChargingStatus,
  getRobotStatus,
} from "utils/UVD/UVDRobotStatusConverter";
import { ON_ROBOT_CHANGED } from "../UnassignedRobots";

export const QUERY_ROBOT_BASIC_INFO = gql(`
  query RobotBasicInfo($robotId: String!) {
    robot(id: $robotId) {
      id
      serialNumber
      ... on UvdRobot {
        active {
          organization {
            id
          }
          nickname
        }
        status {
          batteryMergerPercentage
          job {
            state
            type
          }
          lastTimeActive
        }
        internetPreference
      }
    }
  }
`);

interface Props {
  display?: "datasource" | "default";
}

export const RobotBasicInfo = ({ display = "default" }: Props) => {
  const { robotId } = useParams();
  const [robot, setRobot] = useState<UvdRobot | undefined>(undefined);

  const { data, loading } = useQueryWithSnack(QUERY_ROBOT_BASIC_INFO, {
    variables: { robotId: robotId ?? "" },
    fetchPolicy: "no-cache",
    pollInterval: 10000,
  });

  useEffect(() => {
    setRobot(_.merge(robot, data?.robot) as UvdRobot | undefined);
  }, [data]);

  const { data: subscriptionData } = useSubscription(ON_ROBOT_CHANGED);

  useEffect(() => {
    if (robot && subscriptionData && subscriptionData.robotChanged) {
      const subRobot = subscriptionData.robotChanged as UvdRobot;
      if (robot.id == subRobot.id) {
        setRobot(_.merge(robot, subRobot) as UvdRobot | undefined);
      }
    }
  }, [subscriptionData]);

  const connectionStatus = useUvdConnectionStatus(robot);

  const isOffline = connectionStatus == UVDConnectionStatus.OFFLINE;

  const chargingStatus = robot?.status?.job
    ? getChargingStatus(robot.status.job)
    : null;

  const robotStatus = robot?.status?.job
    ? getRobotStatus(robot.status.job)
    : null;

  const isDocked = robotStatus === "Docked";

  const subheader = useMemo(() => {
    const labelArr = [
      getUvdRobotStatus(robot, connectionStatus),
      chargingStatus?.charging ? "Charging" : undefined,
    ].filter((item) => item);

    return labelArr.join(" - ");
  }, [robot, chargingStatus, connectionStatus]);

  const [editRobotNicknameOpen, setEditRobotNicknameOpen] = useState(false);

  if (loading) return <Skeleton />;

  return (
    <WidgetPaper
      sx={{
        position: "relative",
        overflow: "hidden",
        padding: 6,
        minHeight:
          connectionStatus == UVDConnectionStatus.UNKNOWN ||
          display === "datasource"
            ? 0
            : 62,
      }}
    >
      <UVDPulseIcon
        isOffline={isOffline}
        showPulse={
          !isOffline &&
          connectionStatus !== UVDConnectionStatus.UNKNOWN &&
          !isDocked
        }
        sx={{ marginTop: 2 }}
      />
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="row">
            <Box display="flex" position="relative" mr={4} alignItems="center">
              <UVDIcon
                isOffline={
                  isOffline || connectionStatus === UVDConnectionStatus.UNKNOWN
                }
              />
            </Box>
            <Box>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "left",
                }}
              >
                <Typography
                  variant="h4"
                  color={(theme) =>
                    isOffline || display == "datasource"
                      ? theme.palette.grey[400]
                      : "auto"
                  }
                >
                  {robot?.serialNumber}
                  {data?.robot?.active?.nickname && (
                    <>
                      {" - "}
                      <Typography
                        variant="h4"
                        component="span"
                        style={{
                          fontStyle: "italic",
                          paddingRight: "3px",
                          width: "fit-content",
                        }}
                      >
                        {`''${robot?.active?.nickname}''`}
                      </Typography>
                    </>
                  )}
                </Typography>
                <IconButton
                  onClick={() => {
                    setEditRobotNicknameOpen(true);
                  }}
                  sx={{
                    padding: "0px 0px 2px 5px",
                    color: (theme) => theme.palette.primary.main,
                  }}
                >
                  <EditIcon style={{ width: 20, height: 20 }} />
                </IconButton>
              </div>
              {display !== "datasource" ? (
                <Typography
                  variant="body2"
                  color={(theme) =>
                    isOffline
                      ? theme.palette.grey[400]
                      : theme.palette.grey[600]
                  }
                >
                  {subheader}
                </Typography>
              ) : null}
            </Box>
          </Box>
          {display !== "datasource" && connectionStatus ? (
            <Typography mt={4} variant="bodyS">
              {robot?.internetPreference ? (
                <Typography
                  variant="bodyS"
                  color={(theme) =>
                    isOffline ? theme.palette.grey[400] : "auto"
                  }
                >
                  {robot?.internetPreference === "wifi" ? (
                    <FormattedMessage
                      id="internetPreference.wifi"
                      defaultMessage="Via Wi-Fi"
                    />
                  ) : (
                    <FormattedMessage
                      id="internetPreference.data"
                      defaultMessage="Via data"
                    />
                  )}
                </Typography>
              ) : null}
            </Typography>
          ) : null}
        </Box>
        {display !== "datasource" &&
        connectionStatus !== UVDConnectionStatus.UNKNOWN ? (
          <Box display="flex" flexDirection="row" alignItems="center">
            <Battery
              batteryPercent={robot?.status?.batteryMergerPercentage ?? 0}
              charging={chargingStatus?.charging ?? false}
              pluggedIn={chargingStatus?.pluggedIn ?? false}
              isOffline={isOffline}
              iconSize={60}
            />
            <Typography
              ml={2}
              fontSize={34}
              fontWeight={700}
              color={(theme) =>
                isOffline ? theme.palette.grey[400] : "inherit"
              }
            >
              {Math.floor(robot?.status?.batteryMergerPercentage ?? 0)}%
            </Typography>
          </Box>
        ) : null}
      </Box>
      {editRobotNicknameOpen ? (
        <EditRobotNickname
          open={editRobotNicknameOpen}
          onClose={() => setEditRobotNicknameOpen(false)}
          robot={robot}
        />
      ) : null}
    </WidgetPaper>
  );
};
